import React, { useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { Lead } from "model/lead";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { getPayload, RootState } from "redux/store";
import {
	getActiveLeadEmails,
	getNonActiveLeadEmails,
	getAgentsWithLeads,
} from "redux/selector";
import { validate } from "shared/yup";
import moment from "moment";
import { updateLead } from "redux/slice/leads";
import { updateLeadPhone, createLeadPhone } from "redux/slice/lead-phones";
import {
	updateLeadAnniversary,
	createLeadAnniversary,
} from "redux/slice/lead-anniversaries";
import { deleteLeadTag, createLeadTag } from "redux/slice/lead-tags";
import { fetchLeadProfileSuggestions } from "redux/slice/search-agent";
import { canReviewLeadProfileSuggestions } from "shared/lead-profile-suggestions-permission";
import { LeadFormState } from "../lead-form/definitions/types";
import LeadFormV2 from "../lead-form-v2";
import { getEditLeadValidationSchema } from "../lead-form/definitions/schema";
import { LeadPhone, NewLeadPhone } from "model/lead-phone";
import { LeadAnniversary } from "model/lead-anniversary";
import { LeadTag } from "model/lead-tag";
import { User } from "model/user";

const mapStateToProps = (state: RootState) => {
	return {
		loading: state.leads.loading,
		agents: getAgentsWithLeads(state),
		activeLeadEmails: getActiveLeadEmails(state),
		nonActiveLeadEmails: getNonActiveLeadEmails(state),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			deleteLeadTag,
			updateLead,
			updateLeadPhone,
			createLeadPhone,
			updateLeadAnniversary,
			createLeadAnniversary,
			createLeadTag,
			fetchLeadProfileSuggestions,
		},
		dispatch
	);

interface Props
	extends ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	open: boolean;
	onClose: Function;
	user: User;
	lead: Lead;
	leadPhones?: LeadPhone[];
	leadAnniversaries?: LeadAnniversary[];
	typeTags?: LeadTag[];
}

const EditLeadDialogComponent = (props: Props) => {
	const {
		open,
		onClose,
		user,
		agents,
		lead,
		leadPhones: propLeadPhones,
		leadAnniversaries: anniversaries,
		typeTags,
		activeLeadEmails,
		nonActiveLeadEmails,
		deleteLeadTag,
		updateLead,
		updateLeadPhone,
		createLeadPhone,
		updateLeadAnniversary,
		createLeadAnniversary,
		createLeadTag,
		fetchLeadProfileSuggestions,
	} = props;

	const [submitted, setSubmitted] = useState(false);
	const [leadPhones, setLeadPhones] = useState<LeadPhone[]>([]);

	const wasOpen = useRef(open);

	useEffect(() => {
		if (!wasOpen.current && open) {
			setSubmitted(false);
			setLeadPhones(propLeadPhones || []);
		}
		wasOpen.current = open;
	}, [open, propLeadPhones]);

	const getLeadTypeTags = (typeIds: string[]) => {
		return typeIds.map((typeId) => ({ text: typeId }));
	};

	const submit = async (formState: LeadFormState) => {
		const {
			name,
			email,
			email2,
			phone,
			leadPhones,
			address,
			city,
			state,
			zip,
			birthday,
			transactionAnniversary,
			agentId,
			readiness,
			transactionTimelineStart,
			transactionTimelineEnd,
			justBrowsing,
			transactionPrice,
			workingWithAnotherAgent,
			mortgagePreApproval,
			referralRequested,
			typeIds,
		} = formState;

		setSubmitted(true);
		const validationSchema = getEditLeadValidationSchema({
			lead,
			activeLeadEmails,
			nonActiveLeadEmails,
		});
		const errors = validate(validationSchema, formState);
		const normalizedAgentId =
			agentId === undefined || agentId === null ? undefined : Number(agentId);
		const agent =
			normalizedAgentId !== undefined && !Number.isNaN(normalizedAgentId)
				? agents.find((a) => a.id === normalizedAgentId)
				: undefined;
		const leadTypeTags = getLeadTypeTags(typeIds);
		const updatedLeadAnniversaries: LeadAnniversary[] = [];

		if (birthday) {
			if (!(birthday as LeadAnniversary).startDate) {
				(birthday as LeadAnniversary).startDate = moment();
				(birthday as LeadAnniversary).deleted = false;
				(birthday as LeadAnniversary).lead = lead;
			}
			updatedLeadAnniversaries.push(birthday as LeadAnniversary);
		} else {
			const existingBirthday = anniversaries?.find(
				(anniversary) => anniversary.type.label === "Birthday"
			);
			if (existingBirthday) {
				existingBirthday.deleted = true;
				updatedLeadAnniversaries.push(existingBirthday);
			}
		}

		if (transactionAnniversary) {
			if (!(transactionAnniversary as LeadAnniversary).startDate) {
				(transactionAnniversary as LeadAnniversary).startDate = moment();
				(transactionAnniversary as LeadAnniversary).deleted = false;
				(transactionAnniversary as LeadAnniversary).lead = lead;
			}
			updatedLeadAnniversaries.push(transactionAnniversary as LeadAnniversary);
		} else {
			const existingTransaction = anniversaries?.find(
				(anniversary) => anniversary.type.label === "Transaction Anniversary"
			);
			if (existingTransaction) {
				existingTransaction.deleted = true;
				updatedLeadAnniversaries.push(existingTransaction);
			}
		}

		if (!errors && email) {
			if (typeTags) {
				typeTags.forEach((tag) => {
					deleteLeadTag({ leadTag: tag });
				});
			}

			const savingLead = updateLead({
				lead: {
					...lead,
					id: lead && lead.id,
					name,
					email,
					email2,
					phone,
					address,
					city,
					state,
					zip,
					agent,
					agentId: Number.isNaN(normalizedAgentId)
						? undefined
						: normalizedAgentId,
					readiness,
					transactionTimelineStart,
					transactionTimelineEnd,
					justBrowsing,
					transactionPrice,
					workingWithAnotherAgent,
					mortgagePreApproval,
					referralRequested,
				},
			});
			// An edit can withdraw a pending Search Agent suggestion for the field it changed, so
			// the suggestions are re-read once the save lands. Not awaited: the dialog closes now.
			if (canReviewLeadProfileSuggestions(user)) {
				(async () => {
					// The dispatched thunk resolves with its settled action; only a fulfilled
					// one carries a payload.
					if (getPayload(await savingLead)) {
						fetchLeadProfileSuggestions({ leadId: lead.id });
					}
				})();
			}

			const newLeadPhones: NewLeadPhone[] = [];
			const updatedLeadPhones: LeadPhone[] = [];
			for (const p of leadPhones) {
				if (p && p.id && p.createdOn && p.updatedOn) {
					updatedLeadPhones.push({
						id: p.id,
						leadId: p.leadId,
						type: p.type,
						updatedOn: p.updatedOn,
						createdOn: p.createdOn,
						value: p.value,
						deleted: p.deleted,
					});
				} else {
					newLeadPhones.push({
						leadId: p.leadId,
						type: p.type,
						value: p.value,
						deleted: p.deleted,
					});
				}
			}
			for (const phone of updatedLeadPhones) {
				updateLeadPhone({ leadPhone: phone });
			}
			for (const phone of newLeadPhones) {
				getPayload(await createLeadPhone({ leadPhone: phone }));
			}

			for (const anniversary of updatedLeadAnniversaries) {
				if (anniversary.id) {
					updateLeadAnniversary({ leadAnniversary: anniversary });
				} else {
					createLeadAnniversary({ leadAnniversary: { ...anniversary, lead } });
				}
			}

			for (const tag of leadTypeTags) {
				createLeadTag({
					leadTag: {
						leadId: lead.id,
						text: tag.text,
					},
				});
			}
			onClose();
		}
	};

	const canAddPhone = leadPhones.filter((p) => !p.deleted).length < 3;
	const validationSchema = getEditLeadValidationSchema({
		lead,
		activeLeadEmails,
		nonActiveLeadEmails,
	});

	return (
		<LeadFormV2
			onClose={onClose}
			open={open}
			isEdit={true}
			submitted={submitted}
			leadPhonesList={leadPhones}
			crmPhones={user && user.permissions.crmPhones}
			crmAnniversary={user && user.permissions.crmAnniversary}
			crmTags={user && user.permissions.crmTags}
			canAddPhone={canAddPhone}
			lead={lead}
			isBroker={user && user.permissions.broker && user.permissions.showTeamFeatures}
			isAdmin={user && user.admin}
			agents={agents}
			handleSubmit={submit}
			leadAnniversaries={anniversaries}
			typeTags={typeTags}
			validationSchema={validationSchema}
			defaultAgentId={0}
			extendedLeadFields={user && user.permissions.extendedLeadFields}
		/>
	);
};

export const EditLeadDialog = connect(
	mapStateToProps,
	mapDispatchToProps
)(EditLeadDialogComponent);
